import React, { useEffect, useState } from 'react';
import { Copy, Loader2, Save, TestTube2 } from 'lucide-react';
import client from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import {
  CALLBACK_PATH, DEPOSIT_FLOWS, EXPAY_DEFAULTS, changedFields,
} from '../lib/expay';

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object') return detail.message || fallback;
  return detail || fallback;
};

/**
 * Ayarlar → Finans → ExPay.
 *
 * Bölüm kendi state'ini ve Kaydet düğmesini taşır: ayarlar
 * `provider_configs` satırındadır ve `PATCH /admin/settings` gövdesine hiç
 * girmez. `onEnabledChange` finans bölüm seçicisindeki aktiflik noktasını
 * besler.
 */
const ExpaySettings = ({ paymentBase = '', siteBase = '', onEnabledChange }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(EXPAY_DEFAULTS);
  const [initial, setInitial] = useState(EXPAY_DEFAULTS);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  // Ayarlar okunamadıysa form varsayılanları çizer; kaydetmek onları canlının
  // üzerine yazmak olurdu, bu yüzden bölüm okunana kadar salt okunurdur.
  const [loadError, setLoadError] = useState('');

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const callbackBase = paymentBase || origin;
  const returnBase = siteBase || origin;
  const callbackUrl = `${callbackBase}${CALLBACK_PATH}`;

  const apply = (data) => {
    const merged = { ...EXPAY_DEFAULTS, ...data, private_key: '' };
    setForm(merged);
    setInitial(merged);
    setMeta(data || {});
    onEnabledChange?.(Boolean(data?.enabled));
  };

  useEffect(() => {
    let alive = true;
    client.get('/admin/expay/config')
      .then(({ data }) => {
        if (!alive) return;
        apply(data);
        setLoadError('');
      })
      .catch((error) => {
        if (!alive) return;
        const message = errorText(error, 'Bilinmeyen hata');
        setLoadError(message);
        toast({
          title: 'ExPay ayarları okunamadı',
          description: message,
          variant: 'destructive',
        });
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const pending = changedFields(form, initial, {
    paymentBase: callbackBase, siteBase: returnBase,
  });
  const dirty = Object.keys(pending).length > 0;

  const save = async () => {
    if (loadError || !dirty) return;
    setBusy('save');
    try {
      const { data } = await client.patch('/admin/expay/config', pending);
      apply(data);
      toast({ title: 'ExPay ayarları kaydedildi' });
    } catch (error) {
      toast({
        title: 'ExPay kaydedilemedi',
        description: errorText(error, 'Bilinmeyen hata'),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const test = async () => {
    setBusy('test');
    try {
      const { data } = await client.post('/admin/expay/test');
      toast({ title: data?.message || 'ExPay bağlantısı başarılı' });
    } catch (error) {
      toast({
        title: 'ExPay bağlantı testi başarısız',
        description: errorText(error, 'Bilinmeyen hata'),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text)
      .then(() => toast({ title: 'Kopyalandı' }))
      .catch(() => {});
  };

  if (loading) {
    return (
      <div className="col-span-full flex items-center gap-2 text-sm text-slate-400">
        <Loader2 size={14} className="animate-spin" /> ExPay ayarları yükleniyor…
      </div>
    );
  }

  const flowHint = DEPOSIT_FLOWS.find((flow) => flow.value === form.deposit_flow)?.hint;

  return (
    <>
      <div className="col-span-full mt-1">
        <h3 className="text-sm font-bold text-white">ExPay</h3>
        <p className="mt-1 text-xs text-slate-400">
          Havale/EFT yatırım ve IBAN'a çekim (ExPay <code>TRYP2P</code>). Hem
          yatırımda hem çekimde üyenin <strong>ad, soyad ve TC kimlik
          numarası</strong> sağlayıcıya gönderilir; bunları hesabında kayıtlı
          olmayan üye ExPay'i kullanamaz. Çekim önce <strong>yönetici
          onayından</strong> geçer, sonra sağlayıcıya iletilir. Sonucu
          bildirim değil sağlayıcının durum sorgusu belirler; bekleyen kayıtlar
          arka planda da sorulur. Ayarlar kaydedilince oyuncunun yöntem
          listesindeki <strong>ExPay Havale / EFT</strong> satırı da güncellenir.
        </p>
      </div>

      {loadError && (
        <div className="col-span-full rounded-lg border border-rose-500/40 p-3 text-xs text-rose-200">
          Ayarlar okunamadı ({loadError}); bölüm salt okunur.
        </div>
      )}

      <div className="col-span-full provider-enable">
        <label className="flex items-center gap-2 text-sm font-semibold text-white">
          <input
            type="checkbox"
            checked={!!form.enabled}
            onChange={(e) => set('enabled', e.target.checked)}
          />
          ExPay entegrasyonu aktif
        </label>
        <p className="mt-1 text-[11px] text-slate-400">
          Açmak için public ve private anahtar gerekir.
        </p>
      </div>

      <label className="settings-inline-field">
        API adresi
        <input
          value={form.api_url || ''}
          onChange={(e) => set('api_url', e.target.value)}
          className="admin-input mt-1"
        />
      </label>
      <label className="settings-inline-field">
        Public key (ApiPublic)
        <input
          value={form.public_key || ''}
          onChange={(e) => set('public_key', e.target.value)}
          className="admin-input mt-1"
          autoComplete="off"
        />
      </label>
      <label className="settings-inline-field">
        Private key
        <input
          type="password"
          autoComplete="new-password"
          placeholder={meta.private_key_configured ? 'Kayıtlı — değiştirmek için yazın' : 'ExPay private key'}
          value={form.private_key}
          onChange={(e) => set('private_key', e.target.value)}
          className="admin-input mt-1"
        />
        <span className="mt-1 block text-[11px] text-slate-400">
          Her isteği imzalar (HMAC-SHA512); sunucuya kaydedilir, geri gösterilmez.
        </span>
      </label>
      <label className="settings-inline-field">
        Merchant kimliği (client_merchant_id)
        <input
          value={form.merchant_id || ''}
          onChange={(e) => set('merchant_id', e.target.value)}
          className="admin-input mt-1"
          placeholder="main"
        />
        <span className="mt-1 block text-[11px] text-slate-400">
          Sağlayıcının antifraud kuralları için sitenizi tanımlar.
        </span>
      </label>
      <label className="settings-inline-field">
        Yatırım akışı
        <select
          value={form.deposit_flow || 'h2h'}
          onChange={(e) => set('deposit_flow', e.target.value)}
          className="admin-input mt-1"
        >
          {DEPOSIT_FLOWS.map((flow) => (
            <option key={flow.value} value={flow.value}>{flow.label}</option>
          ))}
        </select>
        {flowHint && (
          <span className="mt-1 block text-[11px] text-slate-400">{flowHint}</span>
        )}
      </label>
      <label className="settings-inline-field">
        Bakiye token'ı (bağlantı testi)
        <input
          value={form.balance_token || ''}
          onChange={(e) => set('balance_token', e.target.value)}
          className="admin-input mt-1"
          placeholder="USDTTRC"
        />
      </label>

      <div className="col-span-full rounded-lg border border-slate-700/60 p-3">
        <div className="text-xs text-slate-300">
          Bildirim adresi (yatırım ve çekim için aynı)
        </div>
        <div className="mt-1 flex items-center gap-2">
          <code className="flex-1 break-all rounded bg-slate-900/60 px-2 py-1.5 text-xs text-slate-200">
            {callbackUrl}
          </code>
          <button type="button" onClick={() => copy(callbackUrl)} className="admin-btn-secondary p-2" aria-label="Kopyala">
            <Copy size={13} />
          </button>
        </div>
        <p className="mt-2 text-[11px] leading-4 text-slate-400">
          Adres <strong>her istekte</strong> ExPay'e gönderilir; sağlayıcı
          panelinde ayrıca tanımlamanız gerekmez. Kaydederken güncel alan
          adından yeniden üretilir. Bildirim imzasız olduğu için bakiyeyi hiçbir
          zaman doğrudan değiştirmez — sonucu sağlayıcıya sorarak öğreniriz.
          {Array.isArray(meta.trusted_ips) && meta.trusted_ips.length > 0 && (
            <>
              {' '}ExPay'in bildirim gönderdiği adresler:
              {' '}<code>{meta.trusted_ips.join(', ')}</code>.
            </>
          )}
        </p>
      </div>

      {[
        { key: 'deposit', title: 'yatırım', cls: 'is-deposit' },
        { key: 'withdraw', title: 'çekim', cls: 'is-withdraw' },
      ].map((side) => (
        <section key={side.key} className={`provider-money ${side.cls}`}>
          <label className="flex items-center gap-2 text-sm font-bold text-white">
            <input
              type="checkbox"
              checked={form[`${side.key}_enabled`] !== false}
              onChange={(e) => set(`${side.key}_enabled`, e.target.checked)}
            />
            ExPay {side.title} açık
          </label>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {['min', 'max'].map((bound) => (
              <label key={bound} className="settings-inline-field">
                {bound === 'min' ? 'Minimum' : 'Maksimum'} {side.title} (₺)
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form[`${side.key}_${bound}_amount`] ?? ''}
                  onChange={(e) => set(`${side.key}_${bound}_amount`, Number(e.target.value))}
                  className="admin-input mt-1"
                />
              </label>
            ))}
          </div>
        </section>
      ))}

      <div className="col-span-full flex flex-wrap gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!!busy || !dirty || !!loadError}
          className="admin-btn-primary inline-flex items-center gap-2 px-4 py-2.5"
        >
          {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          ExPay Ayarlarını Kaydet
        </button>
        <button
          type="button"
          onClick={test}
          disabled={!!busy || !meta.configured}
          className="admin-btn-secondary inline-flex items-center gap-2 px-4 py-2.5"
        >
          {busy === 'test' ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
          Bağlantıyı Test Et
        </button>
      </div>
      <p className="col-span-full text-[11px] leading-4 text-slate-400">
        Bağlantı testi merchant bakiyesini sorgular (işlem üretmez); anahtar
        ya da imza hatalıysa sağlayıcının cevabı olduğu gibi gösterilir.
      </p>
    </>
  );
};

export default ExpaySettings;
